import { Clock } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import type { SaveField } from "../../../hooks/use-auto-save-field";
import { OnlineBookingConfig } from "../hooks/use-online-booking-config";
import { AllowBookingPastClosingToggle } from "./allow-booking-past-closing-toggle";

export function BookingTimingConfig({
  saveField,
  savingFields,
}: {
  saveField: SaveField<OnlineBookingConfig>;
  savingFields: Set<keyof OnlineBookingConfig>;
}) {
  const { control } = useFormContext<OnlineBookingConfig>();
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4" />
        <h4 className="font-medium">{t("onlineBooking.bookingTimeRestrictions")}</h4>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <FormField
          control={control}
          name="leadMinutesFromNow"
          rules={{
            required: t("validation.leadTimeRequired"),
            min: { value: 0, message: t("validation.cannotBeNegative") },
          }}
          render={({ field }) => (
            <FormItem id="field-online-lead-from-now">
              <SettingField
                label={t("onlineBooking.fields.bookingTiming.leadTimeNow.label")}
                helpText={t("onlineBooking.fields.bookingTiming.leadTimeNow.helpText")}
              >
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      {...field}
                      onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <span className="text-sm text-muted-foreground">
                    {t("bookingBehavior.minutes")}
                  </span>
                </div>
              </SettingField>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="leadMinutesFromOpening"
          rules={{
            required: t("validation.leadTimeFromOpeningRequired"),
            min: { value: 0, message: t("validation.cannotBeNegative") },
          }}
          render={({ field }) => (
            <FormItem id="field-online-lead-from-opening">
              <SettingField
                label={t("onlineBooking.fields.bookingTiming.leadTimeOpening.label")}
                helpText={t("onlineBooking.fields.bookingTiming.leadTimeOpening.helpText")}
              >
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      {...field}
                      onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <span className="text-sm text-muted-foreground">
                    {t("bookingBehavior.minutes")}
                  </span>
                </div>
              </SettingField>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="trailMinutesBeforeClosing"
          rules={{
            required: t("validation.trailTimeRequired"),
            min: { value: 0, message: t("validation.cannotBeNegative") },
          }}
          render={({ field }) => (
            <FormItem id="field-online-trail-before-closing">
              <SettingField
                label={t("onlineBooking.fields.bookingTiming.bufferBeforeClosing.label")}
                helpText={t("onlineBooking.fields.bookingTiming.bufferBeforeClosing.helpText")}
              >
                <div className="flex items-center gap-2">
                  <FormControl>
                    <Input
                      type="number"
                      min="0"
                      className="w-20"
                      {...field}
                      onChange={(e) => field.onChange(Number.parseInt(e.target.value, 10) || 0)}
                    />
                  </FormControl>
                  <span className="text-sm text-muted-foreground">
                    {t("bookingBehavior.minutes")}
                  </span>
                </div>
              </SettingField>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <Separator />

      <div id="field-online-allow-past-closing">
        <AllowBookingPastClosingToggle
          saveField={saveField}
          saving={savingFields.has("allowBookingPastClosing")}
        />
      </div>
    </div>
  );
}
